import React from "react";
import { CheckCircle, Activity, AlertTriangle, HeartPulse } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

/**
 * EmailHealthCard — Checkpoint 3.1 processing-health indicator.
 *
 * Reuses the existing status-card visual language established by
 * QuarantineSpikeCard.jsx (left border color + icon + badge keyed off a
 * status enum) rather than inventing a new pattern. Uses a distinct,
 * operational status vocabulary (unknown | healthy | watch | critical |
 * failed) — not the Metric Confidence Standard, since this describes
 * pipeline/system state, not data confidence.
 *
 * DASH-EMAIL-009: `healthy`/`failed` are now real, backed by
 * useEmailDashboardData.js's live processing-health fetch (see that
 * hook's own doc comment). `watch`/`critical` remain defined (still
 * useful visual precedent) but are never produced by this checkpoint's
 * real telemetry, which only ever derives HEALTHY/FAILED/UNKNOWN.
 *
 * DASH-EMAIL-010: added "unknown" and made it the fallback (see STATE_CONFIG
 * lookup below) instead of "healthy" — an unrecognized/missing status
 * previously rendered as a false "Healthy" signal with no ingestion-health
 * telemetry behind it.
 *
 * DASH-EMAIL-008B: visual polish only, no data/wiring change (that was
 * DASH-EMAIL-009's scope, now implemented above). The heading uses the
 * same CardHeader/CardTitle (text-lg) treatment EmailRecentActivityCard.jsx's
 * own heading uses, since the two cards sit side by side under one "Recent
 * Activity & Processing Health" section. "unknown"'s `?`-style HelpCircle
 * icon was replaced with HeartPulse (a real operational-health icon, not
 * "watch"'s Activity or "critical"/"failed"'s AlertTriangle) and adopts the
 * SAME icon color Recent Activity's own Clock icon uses (text-indigo-400)
 * rather than its previous slate tone.
 *
 * DASH-EMAIL-008C: `title` is the PERMANENT name of this operational
 * surface — it must never repeat the current state (previously "Processing
 * Health Unknown" duplicated the adjacent status badge's own "Unknown"
 * label). `label` is the only place a state's current value belongs, so
 * the same title stays correct once real telemetry lets the badge become
 * Healthy/Watch/Critical.
 *
 * DASH-EMAIL-009: `healthy`/`failed` are now real states, driven by
 * useEmailDashboardData.js's live
 * GET /api/email-delivery/dashboard/processing-health fetch (CHECKPOINT
 * grain, email_delivery_discovery_checkpoints -- see that endpoint's own
 * frozen HEALTHY/FAILED/UNKNOWN contract, deliberately no STALE state).
 * `failed` reuses `critical`'s red styling (both represent a disrupted
 * pipeline) rather than inventing a fourth color -- it is its own entry,
 * not a rename of `critical`, since `critical`/`watch` remain distinct,
 * still-unused legacy states this checkpoint's real telemetry never
 * produces. Per DASH-EMAIL-008C's own rule above, EVERY state's `title`
 * must read exactly the same permanent title -- the pre-existing `healthy`/
 * `watch`/`critical` entries had never actually been fixed to match that
 * rule (only `unknown` was ever exercised before this checkpoint); all
 * four are corrected here for consistency, since a state's current value
 * belongs in `label` alone.
 *
 * DASH-EMAIL-025: a tenant can now have more than one real Processing
 * Health checkpoint (one per governed discovery source -- see
 * email_delivery_processing_health_service.py's own docstring), so
 * EmailOverviewView.jsx renders one of these cards per source. The
 * OPTIONAL `sourceLabel` prop lets a caller distinguish multiple cards
 * (e.g. "GHL Discovery Health — Qualification") without this component
 * ever computing that label itself -- it just renders whatever string
 * it's given, appended to the permanent title. Omitting `sourceLabel`
 * renders byte-for-byte the same single-title output this component has
 * always produced, so every existing caller/test that doesn't pass it is
 * completely unaffected.
 *
 * DASH-EMAIL-P1-IMPL-010: title renamed from "Processing Health" to "GHL
 * Discovery Health" -- DASH-EMAIL-P1-CHAR-009 found the prior title
 * readable as whole-EDT-pipeline health (SendGrid webhook receipt/event
 * persistence/Broadcast delivery execution), when the underlying data is
 * exclusively CHECKPOINT-grain GHL discovery-sweep state (see
 * email_delivery_processing_health_service.py's own canonical-source
 * docstring) -- the new title names that scope explicitly. The "Last
 * processed" timestamp label is similarly renamed to "Last successful
 * discovery" (the value is, and remains, `last_success_at` -- no backend
 * field or semantics changed). A new, always-rendered disclosure line
 * states plainly that no staleness/freshness threshold exists today --
 * CHAR-009 confirmed the backend's frozen HEALTHY/FAILED/UNKNOWN contract
 * has no STALE state, so a checkpoint that succeeded once, arbitrarily
 * long ago, and has not run since still reads HEALTHY with no visual
 * distinction from one that just succeeded. This checkpoint does NOT
 * invent a staleness threshold, an age calculation, or a fourth status --
 * it only discloses the limitation that already exists.
 *
 * EMAIL-DISCOVERY-OBSERVABILITY-001: two additive, optional pieces of
 * `health` state, both sourced from useEmailDashboardData.js's own
 * mapProcessingHealthSource -- never a new prop, never a layout change.
 * `lastAttemptAt` is rendered as a second, always-present "Last attempted"
 * line alongside the existing "Last successful discovery" line, letting an
 * operator distinguish the two for the first time (previously identical-
 * looking FAILED and HEALTHY cards gave no way to tell "when did discovery
 * last even try" from "when did it last succeed"). `lastFailureAt` (paired
 * with `lastFailureError`) is durable evidence of the MOST RECENT failure
 * that survives a later successful sweep -- when the CURRENT status is
 * healthy but a `lastFailureAt` exists, a single, low-emphasis line
 * ("Recovered from an earlier discovery failure at <time>") is shown.
 * Deliberately muted (same slate-600 tone as the staleness disclosure,
 * never the red/amber failure palette) so a healthy source never visually
 * reads as failed -- this is a footnote, not a status change. Omitted
 * entirely for FAILED/UNKNOWN status (the active `description` line
 * already covers "currently failing"), and omitted whenever
 * `lastFailureAt` is absent, so a source with no known failure history
 * renders byte-for-byte as it did before this checkpoint.
 */
const STATE_CONFIG = {
  unknown: {
    title: "GHL Discovery Health",
    icon: HeartPulse,
    iconClass: "text-indigo-400",
    borderColor: "border-l-slate-500/50",
    bgClass: "bg-slate-500/5",
    badgeClass: "bg-slate-500/10 text-slate-400",
    label: "Unknown",
  },
  healthy: {
    title: "GHL Discovery Health",
    icon: CheckCircle,
    iconClass: "text-emerald-400",
    borderColor: "border-l-emerald-500/50",
    bgClass: "bg-emerald-500/5",
    badgeClass: "bg-emerald-500/10 text-emerald-400",
    label: "Healthy",
  },
  watch: {
    title: "GHL Discovery Health",
    icon: Activity,
    iconClass: "text-amber-400",
    borderColor: "border-l-amber-500/50",
    bgClass: "bg-amber-500/5",
    badgeClass: "bg-amber-500/10 text-amber-400",
    label: "Watch",
  },
  critical: {
    title: "GHL Discovery Health",
    icon: AlertTriangle,
    iconClass: "text-red-400",
    borderColor: "border-l-red-500/50",
    bgClass: "bg-red-500/5",
    badgeClass: "bg-red-500/10 text-red-400",
    label: "Critical",
  },
  failed: {
    title: "GHL Discovery Health",
    icon: AlertTriangle,
    iconClass: "text-red-400",
    borderColor: "border-l-red-500/50",
    bgClass: "bg-red-500/5",
    badgeClass: "bg-red-500/10 text-red-400",
    label: "Failed",
  },
};

function formatTimestamp(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function EmailHealthCard({ health, sourceLabel, testId = "email-health-card" }) {
  const config = STATE_CONFIG[health?.status] || STATE_CONFIG.unknown;
  const Icon = config.icon;
  // DASH-EMAIL-025: `sourceLabel` is purely additive -- when omitted, the
  // title renders exactly `config.title`, byte-for-byte identical to this
  // component's pre-DASH-EMAIL-025 output.
  const title = sourceLabel ? `${config.title} — ${sourceLabel}` : config.title;

  return (
    <Card
      className={`glass border-white/10 border-l-4 ${config.borderColor} ${config.bgClass}`}
      data-testid={testId}
    >
      {/* DASH-EMAIL-008B: same CardHeader/CardTitle (text-lg) treatment as
          EmailRecentActivityCard.jsx's own heading, since the two cards are
          presented as a visible pair. The status badge stays inline with
          the heading (unchanged position relative to the title). */}
      <CardHeader>
        <CardTitle className="text-lg text-slate-100 flex items-center gap-2">
          <Icon className={`h-5 w-5 ${config.iconClass}`} />
          {title}
          <span
            className={`rounded px-1.5 py-0.5 text-[10px] uppercase tracking-wide ${config.badgeClass}`}
            data-testid="email-health-status-badge"
          >
            {config.label}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {health?.description && (
          <p className="text-xs text-slate-400">{health.description}</p>
        )}
        <p className="text-xs text-slate-500 mt-1">
          Last successful discovery: {formatTimestamp(health?.lastProcessed)}
        </p>
        {/* EMAIL-DISCOVERY-OBSERVABILITY-001: always rendered, independent
            of status, mirroring the "Last successful discovery" line above
            -- lets an operator tell "when did discovery last even attempt
            to run" apart from "when did it last succeed", which was
            previously impossible to distinguish from this card alone. */}
        <p className="text-xs text-slate-500 mt-1" data-testid="email-health-last-attempted">
          Last attempted: {formatTimestamp(health?.lastAttemptAt)}
        </p>
        {/* EMAIL-DISCOVERY-OBSERVABILITY-001: only for a currently-healthy
            source with durable evidence of a prior failure -- deliberately
            muted (same tone as the staleness disclosure below, never the
            red/amber failure palette) so a healthy source never visually
            reads as failed. Absent entirely once no last_failure_at is on
            record, or whenever status isn't healthy (the active
            `description` line above already communicates a current
            failure). */}
        {health?.status === "healthy" && health?.lastFailureAt && (
          <p className="text-xs text-slate-600 mt-1" data-testid="email-health-recovery-note">
            Recovered from an earlier discovery failure at {formatTimestamp(health.lastFailureAt)}
          </p>
        )}
        {/* DASH-EMAIL-P1-IMPL-010: always rendered, independent of status --
            CHAR-009 confirmed no staleness/freshness threshold exists in
            this contract, so a long-dead checkpoint whose last known state
            was a real success still reads HEALTHY with no visual
            distinction from a recent one. This discloses that limitation
            rather than inventing a threshold, an age calculation, or a
            STALE status to paper over it. */}
        <p className="text-xs text-slate-600 mt-1" data-testid="email-health-staleness-disclosure">
          Status reflects the last recorded GHL discovery state. No staleness threshold is currently applied.
        </p>
      </CardContent>
    </Card>
  );
}
